// store/ideasStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { ideasService } from '../services/ideasVault.service';

export interface Idea {
  id: string;
  title: string;
  description: string;
  category: 'video' | 'blog' | 'social' | 'podcast';
  priority: 'high' | 'medium' | 'low';
  tags: string[];
  aiGenerated: boolean;
  status: 'new' | 'in_progress' | 'completed';
  createdAt: Date;
  estimatedEffort: number; // hours
  targetAudience: string[];
  notes?: string;
}

export interface CreateIdeaData {
  title: string;
  description: string;
  category: Idea['category'];
  priority: Idea['priority'];
  tags: string[];
  estimatedEffort: number;
  targetAudience: string[];
  notes?: string;
}

export interface UpdateIdeaData extends Partial<CreateIdeaData> {
  id: string;
}

interface IdeasState {
  // State
  ideas: Idea[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  filterCategory: string;
  filterPriority: string;
  filterStatus: string;

  // Actions
  setSearchTerm: (term: string) => void;
  setFilterCategory: (category: string) => void;
  setFilterPriority: (priority: string) => void;
  setFilterStatus: (status: string) => void;

  // Async Actions
  fetchIdeas: () => Promise<void>;
  createIdea: (data: CreateIdeaData) => Promise<Idea | null>;
  updateIdea: (data: UpdateIdeaData) => Promise<Idea | null>;
  deleteIdea: (id: string) => Promise<boolean>;
  getAISuggestions: (topics?: string[]) => Promise<string[]>;
  getCategories: () => Promise<{ category: string; count: number }[]>;

  // Computed getters
  getFilteredIdeas: () => Idea[];
  getStats: () => {
    total: number;
    highPriority: number;
    mediumPriority: number;
    lowPriority: number;
    byCategory: Record<string, number>;
    byStatus: Record<string, number>;
  };
}

export const useIdeasStore = create<IdeasState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        ideas: [],
        isLoading: false,
        error: null,
        searchTerm: '',
        filterCategory: 'all',
        filterPriority: 'all',
        filterStatus: 'all',

        // Filter actions
        setSearchTerm: (term) => set({ searchTerm: term }),
        setFilterCategory: (category) => set({ filterCategory: category }),
        setFilterPriority: (priority) => set({ filterPriority: priority }),
        setFilterStatus: (status) => set({ filterStatus: status }),

        // Fetch ideas
        fetchIdeas: async () => {
          set({ isLoading: true, error: null });
          try {
            const { searchTerm, filterCategory, filterPriority, filterStatus } = get();

            const filters = {
              searchQuery: searchTerm || undefined, // Make sure to pass searchTerm as searchQuery
              category: filterCategory !== 'all' ? filterCategory : undefined,
              priority: filterPriority !== 'all' ? filterPriority : undefined,
              status: filterStatus !== 'all' ? filterStatus : undefined,
            };

            console.log('Fetching ideas with filters:', filters);

            const ideas = await ideasService.getAll(filters);

            console.log('Fetched ideas:', ideas);

            set({
              ideas,
              isLoading: false,
              error: null
            });
          } catch (error) {
            console.error('Error in fetchIdeas:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch ideas'
            });
          }
        },

        // Create idea
        createIdea: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const newIdea = await ideasService.create(data);
            set((state) => ({
              ideas: [...state.ideas, newIdea],
              isLoading: false,
              error: null
            }));
            return newIdea;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to create idea'
            });
            return null;
          }
        },

        // Update idea
        updateIdea: async (data) => {
          set({ isLoading: true, error: null });
          try {
             // Extract id and remove it from the update payload
            const { id, ...updatePayload } = data;
            const updatedIdea = await ideasService.update(data.id, updatePayload);
            set((state) => ({
              ideas: state.ideas.map(idea =>
                idea.id === data.id ? updatedIdea : idea
              ),
              isLoading: false,
              error: null
            }));
            return updatedIdea;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to update idea'
            });
            return null;
          }
        },

        // Delete idea
        deleteIdea: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await ideasService.delete(id);
            set((state) => ({
              ideas: state.ideas.filter(idea => idea.id !== id),
              isLoading: false,
              error: null
            }));
            return true;
          } catch (error) {
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to delete idea'
            });
            return false;
          }
        },

        // Get AI suggestions
        getAISuggestions: async (topics?: string[]) => {
          try {
            return await ideasService.getAISuggestions(topics);
          } catch (error) {
            console.error('Failed to get AI suggestions:', error);
            throw error;
          }
        },

        // Get categories with counts
        getCategories: async () => {
          try {
            return await ideasService.getCategories();
          } catch (error) {
            console.error('Failed to get categories:', error);
            throw error;
          }
        },

        // Computed getters
        getFilteredIdeas: () => {
          const { ideas, searchTerm, filterCategory, filterPriority, filterStatus } = get();

          return ideas.filter(idea => {
            const matchesSearch = !searchTerm ||
              idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              idea.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
              idea.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesCategory = filterCategory === 'all' || idea.category === filterCategory;
            const matchesPriority = filterPriority === 'all' || idea.priority === filterPriority;
            const matchesStatus = filterStatus === 'all' || idea.status === filterStatus;

            return matchesSearch && matchesCategory && matchesPriority && matchesStatus;
          });
        },

        getStats: () => {
          const { ideas } = get();

          const byCategory = ideas.reduce((acc, idea) => {
            acc[idea.category] = (acc[idea.category] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          const byStatus = ideas.reduce((acc, idea) => {
            acc[idea.status] = (acc[idea.status] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          return {
            total: ideas.length,
            highPriority: ideas.filter(i => i.priority === 'high').length,
            mediumPriority: ideas.filter(i => i.priority === 'medium').length,
            lowPriority: ideas.filter(i => i.priority === 'low').length,
            byCategory,
            byStatus
          };
        },
      }),
      {
        name: 'ideas-storage',
        partialize: (state) => ({
          ideas: state.ideas,
          searchTerm: state.searchTerm,
          filterCategory: state.filterCategory,
          filterPriority: state.filterPriority,
          filterStatus: state.filterStatus,
        }),
      }
    ),
    {
      name: 'ideas-store',
    }
  )
);